import * as React from 'react';
import styled from 'styled-components';
import { deepCompare } from '@agilepoint/utils-common';
import { Grid, Column } from '../../../grid';
import { Textbox } from '../../../textbox';
import { NumericBox, INumericBoxInstance } from '../../../numeric-box';
import { IRGB } from '../types';
import { IColorInputProps } from './color-input-props';
import { getColorFromString, createMutableRef } from '../../../../utils';
import { Typography } from '../../../typography';
import { selectThemeSemantics } from '../../../../styling';

const ColorValueBox = styled(NumericBox).attrs((props) => ({
    showSpinner: false,
    min: 0,
    max: 255,
    step: 1,
    ...props
}))`
    width: 100%;
    & .ap-numericBox {
        height: 30px;
        padding: ${selectThemeSemantics(({ gap }) => gap.m)};
    }
`;

const HexInputBox = styled(Textbox).attrs({ maxLength: 6 })`
    height: 30px;
    width: 100%;
    padding: ${selectThemeSemantics(({ gap }) => gap.m)};
`;

const StyledColumn = styled(Column)`
    padding-left: ${selectThemeSemantics(({ gap }) => gap.m)};
`;

function ensureInRange(value: number, min = 0, max = 255) {
    if (value < min) {
        return min;
    }
    return value > max ? max : value;
}

interface IColorInputState {
    hex: string;
    rgba: IRGB;
}

export class ColorInput extends React.Component<IColorInputProps, IColorInputState> {

    private readonly hexInputRef = createMutableRef<HTMLInputElement>();
    private readonly redInputRef = createMutableRef<INumericBoxInstance>();
    private readonly greenInputRef = createMutableRef<INumericBoxInstance>();
    private readonly blueInputRef = createMutableRef<INumericBoxInstance>();
    private readonly alphaInputRef = createMutableRef<INumericBoxInstance>();
    // const rgbaRef = React.useRef<IRGB>(rgba);
    // const hexRef = React.useRef(hex);

    static getDerivedStateFromProps(props: IColorInputProps, oldState: IColorInputState) {
        const { color } = props;
        const hexChanged = oldState.hex !== color.hex;
        const newRGBA = { r: color.r, g: color.g, b: color.b, a: color.a };
        const rgbaChanged = !deepCompare(newRGBA, oldState.rgba);

        return (hexChanged || rgbaChanged) ? { hex: color.hex, rgba: newRGBA } : null;
    }

    constructor(props: IColorInputProps) {
        super(props);
        this.state = {
            hex: props.color.hex,
            rgba: {
                r: props.color.r,
                g: props.color.g,
                b: props.color.b,
                a: props.color.a!
            }
        };
    }

    componentDidMount() {
        this.setValuesToInput(this.state.rgba, this.props.color.hex);
    }

    componentDidUpdate(prevProps: IColorInputProps) {
        if (prevProps.color.str === this.props.color.str) {
            return;
        }
        this.setValuesToInput(this.state.rgba, this.props.color.hex);
    }

    render() {
        const { opacityEnabled } = this.props;
        const { rgba, hex } = this.state;
        return (
            <Grid noGutter noWrap columnsCount={5}>
                <Column columnSize="1">
                    <Typography.Small>Hex:</Typography.Small>
                    <HexInputBox elementRef={this.hexInputRef} placeholder="Hex"
                        defaultValue={hex} onBlur={this.onHexChanged} />
                </Column>
                <Column columnSize="4">
                    <Grid noGutter noWrap justifyContent="right" columnsCount={opacityEnabled ? 4 : 3}>
                        <StyledColumn columnSize="1">
                            <Typography.Small>Red:</Typography.Small>
                            <ColorValueBox componentRef={this.redInputRef} placeholder="Red"
                                defaultValue={rgba.r.toString()} onValueChange={this.onRGBAChanged} />
                        </StyledColumn>
                        <StyledColumn columnSize="1">
                            <Typography.Small>Green:</Typography.Small>
                            <ColorValueBox componentRef={this.greenInputRef} placeholder="Green"
                                defaultValue={rgba.g.toString()} onValueChange={this.onRGBAChanged} />
                        </StyledColumn>
                        <StyledColumn columnSize="1">
                            <Typography.Small>Blue:</Typography.Small>
                            <ColorValueBox componentRef={this.blueInputRef} placeholder="Blue"
                                defaultValue={rgba.b.toString()} onValueChange={this.onRGBAChanged} />
                        </StyledColumn>
                        {opacityEnabled ?
                            (
                                <StyledColumn columnSize="1">
                                    <Typography.Small>Alpha:</Typography.Small>
                                    <ColorValueBox componentRef={this.alphaInputRef} placeholder="Alpha"
                                        max={100} defaultValue={rgba.a.toString()} onValueChange={this.onRGBAChanged} />
                                </StyledColumn>
                            ) : null as any}
                    </Grid>
                </Column>

            </Grid>
        );
    }

    private setHexRGBAInState = (currentHex: string | null, currentRGBA: IRGB | null, callback?: () => void) => {
        if ((currentHex === null && currentRGBA === null) ||
            this.state.hex === currentHex &&
            (currentRGBA === null || deepCompare(this.state.rgba, currentRGBA))) {
            return;
        }
        this.setState(oldState => ({
            ...oldState,
            hex: currentHex || oldState.hex,
            rgba: currentRGBA || oldState.rgba
        }), callback);
    }

    private onHexChanged = (e: React.FocusEvent<HTMLInputElement>) => {
        const newHexValue = e.target.value;
        const colorInstance = getColorFromString(`#${newHexValue}`);
        if (!colorInstance || colorInstance.hex.includes('NaN')) {
            if (this.hexInputRef.current) {
                this.hexInputRef.current.value = this.state.hex;
            }
            return;
        }
        const newRGBA = { r: colorInstance.r, g: colorInstance.g, b: colorInstance.b, a: 100 };
        this.setValuesToInput(newRGBA, newHexValue);
        this.setHexRGBAInState(newHexValue, newRGBA, () => {
            this.props.onValueChange(newRGBA);
        });
    }

    private onRGBAChanged = () => {
        if (!this.redInputRef.current || !this.greenInputRef.current || !this.blueInputRef.current) {
            return;
        }

        const newRGBA = {
            r: ensureInRange(Number(this.redInputRef.current.getFieldValue())),
            g: ensureInRange(Number(this.greenInputRef.current.getFieldValue())),
            b: ensureInRange(Number(this.blueInputRef.current.getFieldValue())),
            a: ensureInRange(Number(this.alphaInputRef.current ? this.alphaInputRef.current.getFieldValue() : 100), 0, 100)
        };
        this.setHexRGBAInState(null, newRGBA, () => {
            this.props.onValueChange(newRGBA);
        });
    }

    private setValuesToInput = (rgbaValue: IRGB, hexValue: string) => {
        if (this.alphaInputRef.current) {
            this.alphaInputRef.current.setValue(rgbaValue.a.toString());
        }

        if (this.hexInputRef.current) {
            this.hexInputRef.current.value = hexValue;
        }

        if (!this.redInputRef.current ||
            !this.greenInputRef.current ||
            !this.blueInputRef.current) {
            return;
        }

        this.redInputRef.current.setValue(rgbaValue.r.toString());
        this.greenInputRef.current.setValue(rgbaValue.g.toString());
        this.blueInputRef.current.setValue(rgbaValue.b.toString());
    }
}
